import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { Project, ProjectFormData, UserSummary } from "./projectTypes";

export type SaveProjectResult =
  | { success: false; invited: false }
  | { success: true; invited: boolean; clientEmail: string };

export const FREE_PROJECT_LIMIT = 3;

export function useProjects(contractorId?: string) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [clientsById, setClientsById] = useState<Record<string, UserSummary>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    if (!contractorId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setListError(null);

    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("contractor_id", contractorId)
      .order("created_at", { ascending: false });

    if (error) {
      setListError(error.message);
      setLoading(false);
      return;
    }

    const projectData = (data ?? []) as Project[];
    setProjects(projectData);

    const clientIds = Array.from(
      new Set(
        projectData
          .map((project) => project.client_id)
          .filter((id): id is string => id !== null),
      ),
    );

    if (clientIds.length) {
      const { data: clientData, error: clientError } = await supabase
        .from("users")
        .select("id, email, name")
        .in("id", clientIds);

      if (!clientError && clientData) {
        const lookup: Record<string, UserSummary> = {};
        (clientData as UserSummary[]).forEach((client) => {
          lookup[client.id] = client;
        });
        setClientsById(lookup);
      }
    } else {
      setClientsById({});
    }

    setLoading(false);
  }, [contractorId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Resolve an existing client OR send an invite — returns their user ID either way
  const resolveOrInviteClient = useCallback(
    async (
      email: string,
      contractorName: string,
      projectTitle: string,
    ): Promise<{ userId: string; invited: boolean }> => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("invite-client", {
        body: { clientEmail: email, contractorName, projectTitle },
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
      });

      if (data?.error) throw new Error(data.error);
      if (error) throw new Error(error.message ?? "Failed to resolve client.");

      return {
        userId: data.userId as string,
        invited: data.status === "invited",
      };
    },
    [],
  );

  const saveProject = useCallback(
    async (
      formData: ProjectFormData,
      projectId?: string,
      contractorName?: string,
    ): Promise<SaveProjectResult> => {
      if (!contractorId) return { success: false, invited: false };
      if (!formData.title.trim()) {
        setSaveError("Project title is required.");
        return { success: false, invited: false };
      }
      if (!formData.clientEmail.trim()) {
        setSaveError("Client email is required.");
        return { success: false, invited: false };
      }

      setSaving(true);
      setSaveError(null);

      try {
        const clientEmail = formData.clientEmail.trim().toLowerCase();
        const { userId: clientId, invited } = await resolveOrInviteClient(
          clientEmail,
          contractorName ?? "Your contractor",
          formData.title.trim(),
        );

        const payload = {
          title: formData.title.trim(),
          address: formData.address.trim() || null,
          status: formData.status,
          start_date: formData.startDate || null,
          estimated_completion_date: formData.estimatedCompletionDate || null,
          budget: formData.budget ? Number(formData.budget) : null,
          contractor_id: contractorId,
          client_id: clientId,
        };

        if (projectId) {
          const { error } = await supabase
            .from("projects")
            .update(payload)
            .eq("id", projectId);

          if (error) throw error;
        } else {
          const { error } = await supabase.from("projects").insert(payload);

          if (error) throw error;
        }

        await fetchProjects();
        return { success: true, invited, clientEmail };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to save.";
        setSaveError(message);
        return { success: false, invited: false };
      } finally {
        setSaving(false);
      }
    },
    [contractorId, fetchProjects, resolveOrInviteClient],
  );

  const deleteProject = useCallback(
    async (projectId: string) => {
      setListError(null);
      const { error } = await supabase
        .from("projects")
        .delete()
        .eq("id", projectId);

      if (error) {
        setListError(error.message);
        return false;
      }

      await fetchProjects();
      return true;
    },
    [fetchProjects],
  );

  const clearSaveError = () => setSaveError(null);
  const clearListError = () => setListError(null);

  // Active (non-complete) projects count toward the free tier limit
  const activeProjectCount = useMemo(
    () => projects.filter((p) => p.status !== "complete").length,
    [projects],
  );

  const isAtFreeLimit = activeProjectCount >= FREE_PROJECT_LIMIT;

  return {
    projects,
    clientsById,
    loading,
    saving,
    listError,
    saveError,
    activeProjectCount,
    isAtFreeLimit,
    saveProject,
    deleteProject,
    clearSaveError,
    clearListError,
  };
}
